import React, {useState, useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'
import Axios from 'axios'
import Lobby from './Lobby'
import GameBoard from './GameBoard'

const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const getWsUrl = () => {
  if (isLocalDev) return `ws://localhost:${__DEV_BACKEND_PORT__}`
  return `wss://moxtest.app`
}
const getBackendUrl = () => {
  if (isLocalDev) return `http://localhost:${__DEV_BACKEND_PORT__}`
  return 'https://moxtest.app'
}

function shuffle(arr) {
  let a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

let Board = ({onClose}) => {
  let screenWidth = useSelector(state => state.screenWidth)
  let screenHeight = useSelector(state => state.screenHeight)

  // Connection state
  let [phase, setPhase] = useState('lobby') // 'lobby' | 'game'
  let [wid, setWid] = useState(null)
  let [room, setRoom] = useState(null)
  let [players, setPlayers] = useState([])
  let ws = useRef(null)
  let widRef = useRef(null)
  let roomRef = useRef(null)

  // Deck state
  let [decks, setDecks] = useState([])
  let [selectedDeck, setSelectedDeck] = useState(null)
  let selectedDeckRef = useRef(null)

  // Game state
  let [hand, setHand] = useState([])
  let [deckPile, setDeckPile] = useState([])
  let [boardCards, setBoardCards] = useState([])

  // Fetch decks on mount
  useEffect(() => {
    let fetchDecks = async () => {
      try {
        let res = await Axios.get('/api/decks')
        if (res.data.decks?.length) {
          setDecks(res.data.decks)
          return
        }
      } catch {}
      // Fallback to localStorage
      try {
        let saved = localStorage.getItem('mtgDecks')
        if (saved) setDecks(JSON.parse(saved))
      } catch {}
    }
    fetchDecks()
  }, [])

  // WebSocket
  useEffect(() => {
    ws.current = new WebSocket(getWsUrl())

    ws.current.onopen = () => console.log('board ws connected')

    ws.current.onmessage = e => {
      let data
      try { data = JSON.parse(e.data) } catch { return }

      if (data.message === 'welcome') {
        setWid(data.id)
        widRef.current = data.id
      }

      if (data.message === 'lobby') {
        setRoom(data.roomId)
        roomRef.current = data.roomId
        setPlayers(data.players)
      }

      if (data.message === 'on') {
        setPhase('game')
        // Deal hand from selected deck
        let deck = selectedDeckRef.current
        if (deck) {
          let allCards = []
          deck.stacks?.forEach(st => {
            st.cards?.forEach(c => allCards.push(c))
          })
          let shuffled = shuffle(allCards)
          setHand(shuffled.slice(0, 7))
          setDeckPile(shuffled.slice(7))
        }
      }

      if (data.message === 'playCard') {
        setBoardCards(prev => [...prev, data.card])
      }

      if (data.message === 'moveCard') {
        setBoardCards(prev =>
          prev.map(c => (c.id === data.cardId ? {...c, posX: data.posX, posY: data.posY} : c)),
        )
      }

      if (data.message === 'removeCard') {
        setBoardCards(prev => prev.filter(c => c.id !== data.cardId))
      }

      if (data.message === 'error') {
        console.log('ws error:', data.text)
      }
    }

    ws.current.onclose = () => console.log('board ws disconnected')

    return () => {
      if (ws.current) ws.current.close()
    }
  }, [])

  let send = obj => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(obj))
    }
  }

  // Lobby actions
  let host = () => send({action: 'make', wid: widRef.current, name: 'Player'})

  let join = code => {
    if (code.length !== 6) return
    send({action: 'join', roomId: code, wid: widRef.current, name: 'Player'})
  }

  let selectDeck = deck => {
    setSelectedDeck(deck)
    selectedDeckRef.current = deck
    if (roomRef.current) {
      send({action: 'setDeck', roomId: roomRef.current, wid: widRef.current, deckName: deck.name})
    }
  }

  let start = () => send({action: 'start', roomId: roomRef.current})

  // Game actions
  let playCard = (card, clientX, clientY) => {
    let boardCard = {
      id: card.deckCardId || Date.now().toString(),
      card: {name: card.name, url: card.url},
      posX: clientX - 57,
      posY: clientY - 80,
      owner: widRef.current,
    }
    setBoardCards(prev => [...prev, boardCard])
    setHand(prev => prev.filter(c => c !== card))
    send({action: 'playCard', roomId: roomRef.current, card: boardCard})
  }

  let moveCard = (cardId, posX, posY) => {
    setBoardCards(prev => prev.map(c => (c.id === cardId ? {...c, posX, posY} : c)))
    send({action: 'moveCard', roomId: roomRef.current, cardId, posX, posY})
  }

  let pickUpCard = cardId => {
    let boardCard = boardCards.find(c => c.id === cardId)
    if (!boardCard) return
    // Add back to hand with blue outline flag
    let handCard = {
      ...(boardCard.card || {}),
      deckCardId: boardCard.id,
      fromBoard: true,
    }
    setHand(prev => [...prev, handCard])
    setBoardCards(prev => prev.filter(c => c.id !== cardId))
    send({action: 'removeCard', roomId: roomRef.current, cardId})
  }

  let isHost = players.length > 0 && players[0].wid === wid

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: screenWidth,
        height: screenHeight,
        zIndex: 200,
        backgroundColor: '#1a1a1a',
      }}>
      {/* Close button */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 300,
          color: '#888',
          fontSize: 28,
          cursor: 'pointer',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        ×
      </div>

      {phase === 'lobby' ? (
        <Lobby
          room={room}
          players={players}
          isHost={isHost}
          wid={wid}
          decks={decks}
          selectedDeck={selectedDeck}
          onHost={host}
          onJoin={join}
          onStart={start}
          onSelectDeck={selectDeck}
          screenWidth={screenWidth}
          screenHeight={screenHeight}
        />
      ) : (
        <GameBoard
          boardCards={boardCards}
          hand={hand}
          onMoveCard={moveCard}
          onPlayCard={playCard}
          onPickUpCard={pickUpCard}
          screenWidth={screenWidth}
          screenHeight={screenHeight}
        />
      )}
    </div>
  )
}

export default Board
